import { EmailLinkCallback } from '../EmailLinkCallback';
import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { Alert, AlertIcon, Box, Button, FormControl, FormLabel, Heading, HStack, Input, Modal, ModalBody, ModalContent, ModalOverlay, Text, Tooltip, VStack } from '@chakra-ui/react';
import { EmailLoginLinkResult, MutationAuthenticateEmailLoginCodeArgs, Mutation, MutationRequestEmailLoginLinkArgs, MutationPollEmailLoginBrowserArgs } from '../../../generated/graphql';
import { resumeEmailLinkDestination } from '../utils/emailLinkNavigation';
import { AUTHENTICATE_EMAIL_LOGIN_CODE, REQUEST_EMAIL_LOGIN_LINK, POLL_EMAIL_LOGIN_BROWSER } from '../queries';
import { isCompleteSignInCode } from '../utils/signInCode';
import { SignInCodePairs } from './SignInCodePairs';

export interface EmailLinkSignInProps {
  flow?: string;
  allowSignup?: boolean;
  returnPath?: string;
  presentation?: 'page' | 'modal' | 'embedded';
  onClose?: () => void;
  /** Tells the host modal when the page is waiting for the code, so it can hide its first-step options and fit without a scroll. */
  onWaitingChange?: (waiting: boolean) => void;
  /**
   * Optional tenant branding (upstreamed from Shelv, 2026-09-11): a header (e.g. the logo) shown
   * above the heading on both steps, plus the first step's heading, description and submit label in
   * the tenant's own words. The waiting/code step keeps the standard copy so every tenant's code
   * flow reads the same. Omit it and nothing changes. Used on full pages, not inside the login modal.
   */
  branding?: { header?: React.ReactNode; title: string; description: string; submitLabel: string };
}

/**
 * Presentation is independent of the tenant's credential/profile policy.
 *
 * Code first (2026-09-11): in `link_and_code` the email carries a sign-in link AND a four-digit
 * code. Opening the link signs in the device that opens it; typing the code here signs in THIS
 * page. Polling continues so a same-browser open still finishes on its own. No browser
 * reference is shown — the code is the cross-device proof.
 */
export function EmailLinkSignIn({ flow = 'account', allowSignup = false, returnPath = '/', presentation = 'page', onClose = () => {}, onWaitingChange, branding }: EmailLinkSignInProps) {
  const apollo = useApolloClient();
  const [browserRequest, setBrowserRequest] = useState<{ token: string; expiresAt: number }>();
  const [waitingError, setWaitingError] = useState('');
  const [code, setCode] = useState('');
  const inFlight = useRef(false);
  const [codeAvailable, setCodeAvailable] = useState(false);
  const [codeResult, setCodeResult] = useState<EmailLoginLinkResult>();
  const [verifyCode, { loading: verifyingCode, error: codeError }] = useMutation<Pick<Mutation, 'authenticateEmailLoginCode'>, MutationAuthenticateEmailLoginCodeArgs>(AUTHENTICATE_EMAIL_LOGIN_CODE);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  useEffect(() => { onWaitingChange?.(sent); }, [sent, onWaitingChange]);
  useEffect(() => () => onWaitingChange?.(false), [onWaitingChange]);
  const [retryAt, setRetryAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [request, { loading, error }] = useMutation<Pick<Mutation, 'requestEmailLoginLink'>, MutationRequestEmailLoginLinkArgs>(REQUEST_EMAIL_LOGIN_LINK);
  useEffect(() => { const timer = window.setInterval(() => setNow(Date.now()), 1000); return () => window.clearInterval(timer); }, []);
  useEffect(() => {
    let stopped = false;
    let timer: number;
    const navigate = resumeEmailLinkDestination;
    const onStorage = (event: StorageEvent) => {
      if (event.key === 'auth_token' && event.newValue) navigate(returnPath);
    };
    window.addEventListener('storage', onStorage);
    async function poll() {
      if (stopped || !browserRequest) return;
      if (localStorage.getItem('auth_token')) { navigate(returnPath); return; }
      if (Date.now() >= browserRequest.expiresAt) { setWaitingError('This browser request has expired. Request a new link to sign in here.'); return; }
      try {
        const { data } = await apollo.mutate<Pick<Mutation, 'pollEmailLoginBrowser'>, MutationPollEmailLoginBrowserArgs>({ mutation: POLL_EMAIL_LOGIN_BROWSER, variables: { token: browserRequest.token } });
        if (stopped) return;
        if (data?.pollEmailLoginBrowser.auth?.token) {
          if (!localStorage.getItem('auth_token')) localStorage.setItem('auth_token', data.pollEmailLoginBrowser.auth.token);
          navigate(data.pollEmailLoginBrowser.returnPath || returnPath);
          return;
        }
        if (data?.pollEmailLoginBrowser.status === 'EXPIRED') { setWaitingError('This browser request is no longer available. Request a new link.'); return; }
        setWaitingError('');
      } catch { if (!stopped) setWaitingError('Unable to check sign-in yet. Retrying automatically…'); }
      if (!stopped) timer = window.setTimeout(poll, 3000);
    }
    if (browserRequest) timer = window.setTimeout(poll, 3000);
    return () => { stopped = true; window.clearTimeout(timer); window.removeEventListener('storage', onStorage); };
  }, [apollo, browserRequest, returnPath]);
  const cooldown = Math.max(0, Math.ceil((retryAt - now) / 1000));
  async function submit(event: FormEvent) {
    event.preventDefault();
    try {
      const { data } = await request({ variables: { input: { email: email.trim(), flow, returnPath } } });
      if (data?.requestEmailLoginLink.accepted) {
        setSent(true);
        setCodeAvailable(!!data.requestEmailLoginLink.codeAvailable);
        setCode('');
        setWaitingError('');
        const response = data.requestEmailLoginLink;
        setBrowserRequest(response.browserToken ? { token: response.browserToken, expiresAt: Date.now() + 10 * 60 * 1000 } : undefined);
        setRetryAt(Date.now() + data.requestEmailLoginLink.retryAfterSeconds * 1000);
      }
    } catch { /* Apollo supplies the visible error. */ }
  }
  // Submitted automatically when the fourth digit lands (SignInCodePairs passes the code in);
  // the button is the fallback. inFlight stops a second submit while the first is on the wire.
  async function verifyBackupCode(entered?: string) {
    const value = entered ?? code;
    if (!browserRequest || !isCompleteSignInCode(value) || verifyingCode || inFlight.current) return;
    inFlight.current = true;
    try {
      const { data } = await verifyCode({ variables: { browserToken: browserRequest.token, code: value } });
      if (data?.authenticateEmailLoginCode) { setBrowserRequest(undefined); setCodeResult(data.authenticateEmailLoginCode); }
    } catch { setCode(''); /* Apollo renders the verification error. */ }
    finally { inFlight.current = false; }
  }
  if (codeResult) return <EmailLinkCallback initialResult={codeResult} defaultPath={returnPath} />;
  const content = <VStack as="form" onSubmit={submit} spacing={sent ? 3 : 6} align="stretch" w="full" maxW="430px" mx="auto">
    {branding?.header}
    <Heading as="h1" size={presentation === 'embedded' ? 'md' : 'xl'} textAlign="center">{sent ? 'Check Your Email' : branding?.title || <>One Email,<br />One Link.</>}</Heading>
    <Text textAlign="center">{sent ? codeAvailable ? `If sign-in is available for ${email}, an email will arrive. Type the code from the email here, or open its link on this device.` : `If sign-in is available for ${email}, you’ll receive a personal link. Open it on this device to continue.` : branding?.description || (allowSignup ? 'Enter your email to sign in or get started. No password to remember.' : 'Enter your email to sign in. No password to remember.')}</Text>
    {waitingError && <Alert status="warning"><AlertIcon />{waitingError}</Alert>}
    {error && <Alert status="error"><AlertIcon />{error.message}</Alert>}
    {!sent && <FormControl isRequired>
      <FormLabel htmlFor="email-link-address">Email address</FormLabel>
      <Input id="email-link-address" placeholder={branding ? 'Enter email address' : undefined} type="email" autoComplete="email" value={email} onChange={event => setEmail(event.target.value)} maxLength={254} size="lg" autoFocus />
    </FormControl>}
    {sent && codeAvailable && browserRequest && <FormControl as={VStack} spacing={2} align="center">
      <FormLabel htmlFor="email-sign-in-code" m={0} fontSize="sm">Code from the email</FormLabel>
      <SignInCodePairs id="email-sign-in-code" label="Code from the email" value={code} onChange={setCode} onComplete={entered => void verifyBackupCode(entered)} isDisabled={verifyingCode} />
      <Tooltip label="Checks the code and signs in this page through the same sign-in flow. It is checked automatically as soon as all four digits are in. Nothing is sent."><Button type="button" colorScheme="green" w="full" maxW="16rem" isDisabled={!isCompleteSignInCode(code)} isLoading={verifyingCode} loadingText="Checking code…" onClick={() => void verifyBackupCode()}>Verify and continue</Button></Tooltip>
      {codeError && <Alert mt={3} status="error"><AlertIcon />{codeError.message}</Alert>}
    </FormControl>}
    {!sent && <Tooltip label="Request a personal sign-in link by email.">
      <Button type="submit" size="lg" colorScheme="green" isLoading={loading} loadingText="Requesting link…">{branding?.submitLabel || 'Send me a link'}</Button>
    </Tooltip>}
    {/* Waiting state: the same compact row as the SMS page, so the modal never needs a scroll. */}
    {sent && <HStack spacing={2} justify="center" flexWrap="wrap">
      <Tooltip label="Request another email. The previous link and code stop working."><Button type="submit" size="sm" variant="outline" isLoading={loading} loadingText="Requesting…" isDisabled={cooldown > 0}>{cooldown ? `Resend in ${cooldown}s` : 'Resend email'}</Button></Tooltip>
      <Tooltip label="Use a different email address; this does not change an existing account."><Button size="sm" variant="ghost" onClick={() => { setSent(false); setEmail(''); setBrowserRequest(undefined); setWaitingError(''); }}>Change email</Button></Tooltip>
    </HStack>}
    {sent && <Text fontSize="xs" color="gray.500" textAlign="center" role="status">Check your spam folder too. This request does not confirm delivery. Links and codes work once and expire in ten minutes.</Text>}
  </VStack>;
  if (presentation === 'embedded') return content;
  if (presentation === 'modal') return <Modal isOpen onClose={onClose} isCentered><ModalOverlay /><ModalContent mx={4}><ModalBody py={10}>{content}<Button mt={6} variant="ghost" title="Close sign-in" onClick={onClose}>Close</Button></ModalBody></ModalContent></Modal>;
  return <Box minH="100dvh" display="flex" alignItems="center" p={{ base: 6, md: 12 }}>{content}</Box>;
}
